import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import adminConsoleService from '@services/adminConsoleService';

interface Position {
  Id: number;
  Name: string;
  Description: string;
  Rate: number;
}

type ModalMode = 'add' | 'edit' | 'delete';

// ─── Modal ────────────────────────────────────────────────────────────────────

interface PositionModalProps {
  mode: ModalMode;
  position: Partial<Position>;
  saving: boolean;
  error: string | null;
  onChange: (field: 'Name' | 'Rate', value: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}

const MODAL_TITLES: Record<ModalMode, string> = {
  add: 'Add New Position',
  edit: 'Edit Position',
  delete: 'Delete Position',
};

const PositionModal: React.FC<PositionModalProps> = ({
  mode,
  position,
  saving,
  error,
  onChange,
  onConfirm,
  onClose,
}) =>
{
  const isDelete = mode === 'delete';

  return createPortal(
    <div className="admin-console-portal fixed inset-0 z-[10000] flex items-center justify-center bg-black/30">
      <div className="bg-white rounded-lg shadow-2xl w-[400px] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0]">
          <h2 className="text-[16px] font-normal text-[#5697b3]">{MODAL_TITLES[mode]}</h2>
          <button onClick={onClose} className="text-[#bbb] hover:text-[#555]">
            <span className="material-icons text-[18px]">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-4">
          {error && (
            <div className="px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828]">
              {error}
            </div>
          )}

          {/* Position Name */}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#A5A7AC]">Position Name:</label>
            {isDelete ? (
              <div className="bg-[#f5f5f5] border border-[#e0e0e0] rounded px-3 py-1.5 text-[12px] text-[#333]">
                {position.Name || ''}
              </div>
            ) : (
              <input
                autoFocus
                type="text"
                value={position.Name || ''}
                onChange={e => onChange('Name', e.target.value)}
                className="border border-[#d0d0d0] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb]"
              />
            )}
          </div>

          {/* Position Rate */}
          <div className="flex flex-col gap-1">
            <label className="text-[12px] text-[#A5A7AC]">Position Rate:</label>
            {isDelete ? (
              <div className="bg-[#f5f5f5] border border-[#e0e0e0] rounded px-3 py-1.5 text-[12px] text-[#333]">
                {position.Rate ?? 0}
              </div>
            ) : (
              <input
                type="number"
                min="0"
                step="0.01"
                value={position.Rate ?? ''}
                onChange={e => onChange('Rate', e.target.value)}
                className="border border-[#d0d0d0] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb]"
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#e0e0e0]">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#aaa] text-white rounded hover:bg-[#999] dark:bg-[#5c6060] dark:hover:brightness-[0.85] transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={saving}
            className="px-4 py-1.5 text-[12px] bg-[#62aacb] text-white rounded hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] transition-colors disabled:opacity-50"
          >
            {saving ? '…' : 'Confirm'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export const PositionsTab: React.FC = () =>
{
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const [modal, setModal] = useState<ModalMode | null>(null);
  const [selected, setSelected] = useState<Partial<Position>>({});
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  useEffect(() =>
  {
    adminConsoleService
      .getPositions()
      .then(setPositions)
      .catch(() => setError('Failed to load positions.'))
      .finally(() => setLoading(false));
  }, []);

  const openAdd = () =>
  {
    setSelected({ Name: '', Description: '', Rate: 0 });
    setModalError(null);
    setModal('add');
  };

  const openEdit = (p: Position) =>
  {
    setSelected({ ...p });
    setModalError(null);
    setModal('edit');
  };

  const openDelete = (p: Position) =>
  {
    setSelected({ ...p });
    setModalError(null);
    setModal('delete');
  };

  const handleChange = (field: 'Name' | 'Rate', value: string) =>
  {
    setSelected(prev => ({
      ...prev,
      [field]: field === 'Rate' ? value : value,
    }));
  };

  const handleConfirm = async () =>
  {
    if (!modal) return;
    if ((modal === 'add' || modal === 'edit') && !String(selected.Name ?? '').trim())
    {
      setModalError('Position Name is required.');
      return;
    }
    setSaving(true);
    setModalError(null);
    try
    {
      await adminConsoleService.updatePosition({
        PositionId: modal === 'add' ? null : (selected.Id ?? null),
        Name: String(selected.Name ?? '').trim(),
        Description: selected.Description ?? '',
        Rate: String(selected.Rate ?? 0),
        Action: modal,
      });

      // Refresh list
      const updated = await adminConsoleService.getPositions();
      setPositions(updated);
      setModal(null);
    }
    catch
    {
      setModalError('Operation failed. Please try again.');
    }
    finally
    {
      setSaving(false);
    }
  };

  return (
    <div className="px-6 py-5">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[16px] font-normal text-[#5697b3]">Positions</h3>
        <button
          onClick={openAdd}
          className="text-[12px] text-[#5697b3] hover:underline items-center flex gap-1"
        >
          <span className="material-icons text-[20px] text-[#00000066] dark:text-[#beb7ac] ">
            add_circle_outline
          </span>
          Add New Position
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 px-3 py-2 bg-[#fdecea] border border-[#e57373] rounded text-[12px] text-[#c62828] flex items-center justify-between gap-2">
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <span className="material-icons text-[14px]">close</span>
          </button>
        </div>
      )}

      {/* Table */}
      <div className="border border-[#ddd] rounded-sm bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center gap-2 p-6 text-[12px] text-[#aaa]">
            <span className="material-icons animate-spin text-[16px] text-[#62aacb]">refresh</span>
            Loading…
          </div>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-[#f5f5f5] border-b border-[#e0e0e0]">
                <th className="text-left px-4 py-2 text-[#555] font-medium">Position</th>
                <th className="text-center px-4 py-2 text-[#555] font-medium w-[100px]">Rate</th>
                <th className="text-right px-4 py-2 text-[#555] font-medium w-[130px]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {positions.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center py-6 text-[#aaa]">
                    No positions found.
                  </td>
                </tr>
              ) : (
                positions.map((p, i) => (
                  <tr
                    key={p.Id}
                    className={`border-b border-[#f0f0f0] ${i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} hover:bg-[#f0f6fb] transition-colors`}
                  >
                    <td className="px-4 py-2 text-[#5697b3]">{p.Name}</td>
                    <td className="px-4 py-2 text-center text-[#555]">{p.Rate}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          onClick={() => openEdit(p)}
                          className="px-3 py-1 bg-[#62aacb] text-white rounded text-[11px] hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => openDelete(p)}
                          className="px-3 py-1 bg-[#e57373] text-white rounded text-[11px] hover:bg-[#d32f2f] dark:bg-[#8d312c] dark:hover:brightness-[0.85] transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <PositionModal
          mode={modal}
          position={selected}
          saving={saving}
          error={modalError}
          onChange={handleChange}
          onConfirm={handleConfirm}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};
